import React, { useCallback } from 'react';
import { CSSObject, Global } from '@emotion/react';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import ScrollViewWithBars from '@cvent/carina/components/ScrollViewWithBars/ScrollViewWithBars';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import Link from 'next/link';
import NavItemIndicator from '@components/layout/NavItemIndicator';
import { Z_INDEX } from '@components/shared/zIndex';
import { Keys } from '@tools/KeyConstants';
import ProfileLogout from './ProfileLogout';
import isAbsoluteUrl from './isAbsoluteUrl';

interface Props {
  showMobileMenu?: boolean;
  navHeight?: number;
  eventTitle: string;
  pages: unknown[]; // Fill in
  attendeeName: string;
  avatarName: string;
  attendeeEmail: string;
  attendeeProfilePhotoSrc?: string;
  onLogout: () => void;
  flyoutControls: React.ReactNode[];
  unreadReportCount?: number;
  pendingConnectionsCount?: number;
}

const useStyles = ({
  navHeight = 0,
  showMobileMenu
}: {
  navHeight?: number;
  showMobileMenu?: boolean;
}): Record<string, CSSObject> => {
  const { borderColor, size, font, backgroundColor } = useTheme();

  return {
    mobileNavMenu: {
      width: '100%',
      height: showMobileMenu ? `calc(100% - ${navHeight}px)` : 0,
      transition: '0.3s ease height',
      backgroundColor: backgroundColor.brand.base,
      position: 'fixed',
      top: navHeight,
      left: 0,
      zIndex: Z_INDEX.MOBILE_NAV_MENU
    },
    separator: {
      borderBottom: 'solid',
      borderBottomWidth: size.border.base,
      borderBottomColor: borderColor.soft,
      marginTop: 12
    },
    linkWrapper: {
      marginTop: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    linkText: {
      fontSize: font.base.size.m,
      color: font.color.base,
      textDecoration: 'none'
    },
    pageGroup: {
      borderBottom: 'solid',
      borderBottomWidth: size.border.base,
      borderBottomColor: borderColor.soft,
      marginTop: 16
    },
    eventTitle: {
      fontSize: font.base.size.m,
      marginTop: 16,
      marginBottom: 20
    },
    scrollView: {
      marginLeft: 24,
      marginRight: 24
    },
    sectionTitle: {
      fontSize: font.base.size.xs,
      margin: '16px 0 0 0'
    },
    mobileTitle: {
      fontSize: font.base.size.m
    }
  };
};

function MobileNavLink({ title, testID, url, indicator, onClick }) {
  const styles = useStyles({});
  return (
    <Row>
      <Col padding={0}>
        <div css={styles.linkWrapper}>
          <Link
            href={url?.href}
            passHref
            css={styles.linkText}
            target={isAbsoluteUrl(url?.href) ? '_blank' : '_self'}
            {...injectTestId(testID)}
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
            onKeyDown={event => {
              if (event.key === Keys.SPACE) {
                event.preventDefault();
                window.location.href = `/hub${url?.href}`;
              }
            }}
          >
            {title}
          </Link>
          {indicator && <NavItemIndicator text={indicator} testID={`${testID}-badge`} />}
        </div>
      </Col>
    </Row>
  );
}

function MobileNavHeading({ title }: { title: string }): JSX.Element {
  const { sectionTitle } = useStyles({});
  return (
    <Row>
      <Col padding={0}>
        <h6 css={sectionTitle}>{title}</h6>
      </Col>
    </Row>
  );
}

function MobileNavPages({ flyoutControls, pages, profileLogoutComponent, getNavItemIndicator }): JSX.Element {
  const styles = useStyles({});

  const pageGroups = pages.map((page, index) => {
    // Combine items and subItems to support grouped pages.
    const pageItems = page?.items.map(item => [item, ...(item?.subItems || [])]).flat();

    const items =
      pageItems.length > 1 ? (
        pageItems.map(item => (
          <div key={`mobile-nav-link-${item?.title.replace(/\s/g, '-').toLowerCase()}`}>
            {item.url ? (
              <MobileNavLink
                title={item?.title}
                url={item?.url}
                testID={item?.testID}
                indicator={getNavItemIndicator(item)}
                onClick={item?.onClick}
              />
            ) : (
              <MobileNavHeading title={item?.title} />
            )}
          </div>
        ))
      ) : (
        <div key={`mobile-nav-link-${page?.title.replace(/\s/g, '-').toLowerCase()}`}>
          <MobileNavLink
            title={page?.title}
            url={page?.url}
            testID={page?.testID}
            indicator={getNavItemIndicator(page)}
            onClick={page?.onClick}
          />
        </div>
      );

    return (
      <div key={`mobile-nav-page-group-${index}`}>
        <div css={styles.pageGroup} />
        {(page?.items?.length > 1 || (page?.items?.length === 1 && page?.items[0].subItems)) && (
          <Row>
            <Col padding={0}>
              <h6 css={styles.sectionTitle}>{page.title}</h6>
            </Col>
          </Row>
        )}
        {items}
      </div>
    );
  });

  return (
    <>
      {flyoutControls.map(
        (flyout: React.ReactNode, index: number): JSX.Element => (
          <Row key={index} margin={{ top: 12 }}>
            <Col
              flex={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              padding={0}
              width="content"
            >
              {flyout}
            </Col>
          </Row>
        )
      )}
      {pageGroups}
      <MobileNavSeparator />
      {profileLogoutComponent}
    </>
  );
}

function MobileNavSeparator() {
  const styles = useStyles({});
  return <div css={styles.separator} />;
}

function TabletNavPages({ pages, profileLogoutComponent, getNavItemIndicator }) {
  const allPageItems = pages.flatMap(page =>
    !page.items
      ? page
      : [page].concat(page.items.flatMap(item => (!item.subItems ? item : [item].concat(item.subItems))))
  );

  const pagesMidpoint = Math.round(allPageItems.length / 2);
  const columnOnePages = allPageItems.slice(0, pagesMidpoint);
  const columnTwoPages = allPageItems.slice(pagesMidpoint);
  // prevents a header from sitting at the bottom of the left column alone
  while (columnOnePages.length && !columnOnePages[columnOnePages.length - 1]?.url) {
    columnTwoPages.unshift(columnOnePages.pop());
  }

  const formatColumnContent = columnPages =>
    columnPages.length > 1 ? (
      columnPages.map((item, index) => (
        <div key={item?.title}>
          {item.items && index !== 0 ? <MobileNavSeparator /> : null}
          {item.url ? (
            <MobileNavLink
              title={item?.title}
              url={item?.url}
              testID={item?.testID}
              indicator={getNavItemIndicator(item)}
              onClick={item?.onClick}
            />
          ) : (
            <MobileNavHeading title={item?.title} />
          )}
        </div>
      ))
    ) : (
      <>
        <MobileNavSeparator />
        <div>
          <MobileNavLink
            title={columnPages[0]?.title}
            url={columnPages[0]?.url}
            testID={columnPages[0]?.testID}
            indicator={getNavItemIndicator(columnPages[0])}
            onClick={columnPages[0]?.onClick}
          />
        </div>
      </>
    );

  return (
    <Row>
      <Col padding={0} width={1 / 2}>
        <div>{formatColumnContent(columnOnePages)}</div>
      </Col>
      <Col padding={{ start: 32 }} width={1 / 2}>
        {columnTwoPages.length ? <div>{formatColumnContent(columnTwoPages)}</div> : null}
        <MobileNavSeparator />
        {profileLogoutComponent}
      </Col>
    </Row>
  );
}

function MobileNavMenu({
  showMobileMenu = false,
  navHeight,
  eventTitle,
  flyoutControls,
  pages,
  attendeeName,
  avatarName,
  attendeeEmail,
  attendeeProfilePhotoSrc,
  onLogout,
  unreadReportCount,
  pendingConnectionsCount
}: Props): JSX.Element {
  const styles = useStyles({
    navHeight,
    showMobileMenu
  });
  const { isM } = useBreakpoints();

  const profileLogoutComponent = (
    <ProfileLogout
      attendeeName={attendeeName}
      avatarName={avatarName}
      attendeeEmail={attendeeEmail}
      attendeeProfilePhotoSrc={attendeeProfilePhotoSrc}
      onLogout={onLogout}
      forMobileTakeover
    />
  );

  const getNavItemIndicator = useCallback(
    pageConfig => {
      if (pageConfig?.testID === 'navigation-menu-connections') {
        return pendingConnectionsCount;
      }
      if (pageConfig?.testID === 'navigation-menu-reported-incidents') {
        return unreadReportCount;
      }
      return undefined;
    },
    [pendingConnectionsCount, unreadReportCount]
  );

  return (
    <div css={styles.mobileNavMenu} {...injectTestId('mobile-navigation-menu')}>
      <Global
        styles={{
          body: {
            overflow: showMobileMenu ? 'hidden' : 'visible'
          }
        }}
      />
      <ScrollViewWithBars>
        <div css={styles.scrollView}>
          <div css={styles.eventTitle}>{eventTitle}</div>
          {!isM ? (
            <MobileNavPages
              flyoutControls={flyoutControls}
              pages={pages}
              profileLogoutComponent={profileLogoutComponent}
              getNavItemIndicator={getNavItemIndicator}
            />
          ) : (
            <TabletNavPages
              pages={pages}
              profileLogoutComponent={profileLogoutComponent}
              getNavItemIndicator={getNavItemIndicator}
            />
          )}
        </div>
      </ScrollViewWithBars>
    </div>
  );
}

export default MobileNavMenu;
